import { ref, onMounted, onUnmounted } from 'vue';
import { BuildVersionService } from '@/application/services/BuildVersionService';

// Constante con la clave para almacenar en localStorage
// Añadimos el prefijo del entorno para separar QA y PROD
const environment = import.meta.env.VITE_API_BASE_ENVIRONMENT || 'PROD';
const LOCAL_BUILD_KEY = `app_build_number_${environment}`;

// Función para recargar la página
const reloadPage = () => {
  const location = window.location as any;
  location.reload();
};


// Se cambia el tipo de exportación para garantizar que sea correctamente identificada
const useBuildVersion = () => {
  const currentBuildNumber = ref<number>(0);
  const localBuildNumber = ref<number>(0);
  const needsRefresh = ref<boolean>(false);
  const isCheckingBuild = ref<boolean>(false);
  const buildService = BuildVersionService.getInstance();
  let unsubscribe: (() => void) | null = null;

  /**
   * Obtiene el número de build almacenado localmente
   */
  const getLocalBuildNumber = (): number => {
    const savedBuild = localStorage.getItem(LOCAL_BUILD_KEY);
    return savedBuild ? parseInt(savedBuild, 10) : 0;
  };

  /**
   * Función para depuración que muestra los buildNumbers de todos los entornos
   */
  const debugBuildNumbers = (): void => {
    const qa = localStorage.getItem('app_build_number_QA') || '0';
    const dev = localStorage.getItem('app_build_number_DEVELOPMENT') || '0';
    const prod = localStorage.getItem('app_build_number_PROD') || '0';
    
  };

  /**
   * Guarda el número de build actual en localStorage
   */
  const saveLocalBuildNumber = (buildNumber: number): void => {
    localStorage.setItem(LOCAL_BUILD_KEY, buildNumber.toString());
    localBuildNumber.value = buildNumber;
  };

  /**
   * Compara la versión local con la del servidor y determina si se necesita actualizar
   */
  const checkBuildVersion = async (): Promise<void> => {
    try {
      isCheckingBuild.value = true;
      
      // Obtener el número de build actual desde el servidor
      const serverBuild = await buildService.getCurrentBuildNumber();
      currentBuildNumber.value = serverBuild;
      
      // Obtener el número de build guardado localmente
      const localBuild = getLocalBuildNumber();
      localBuildNumber.value = localBuild;
      
      // Comparar versiones
      if (localBuild === 0) {
        // Primera vez, simplemente guardar
        saveLocalBuildNumber(serverBuild);
      } else if (serverBuild > localBuild) {
        // Hay una nueva versión
        needsRefresh.value = true;
        // Actualizar automáticamente
        refreshApp();
      }
    } catch (error) {
      console.error('Error al verificar la versión del build:', error);
    } finally {
      isCheckingBuild.value = false;
    }
  };

  /**
   * Actualiza la aplicación limpiando el caché y recargando
   */
  const refreshApp = (): void => {
    if ('caches' in window) {
      // Limpiar el caché
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
        
        // Actualizar el número de build local
        saveLocalBuildNumber(currentBuildNumber.value);
        
        // Recargar la página
        reloadPage();
      });
    } else {
      // Navegador no soporta Cache API, solo recargar
      saveLocalBuildNumber(currentBuildNumber.value);
      reloadPage();
    }
  };

  // Configurar el listener para cambios en tiempo real
  const setupBuildListener = (): void => {
    unsubscribe = buildService.subscribeToBuildChanges((newBuildNumber) => {
      currentBuildNumber.value = newBuildNumber;
      
      // Si el nuevo número es mayor que el local, necesitamos actualizar
      if (newBuildNumber > localBuildNumber.value) {
        needsRefresh.value = true;
        // Actualizar automáticamente cuando se detecta cambio en tiempo real
        refreshApp();
      }
    });
  };

  // Inicialización al montar el componente
  onMounted(async () => {
    await checkBuildVersion();
    setupBuildListener();
  });

  // Limpieza al desmontar
  onUnmounted(() => {
    if (unsubscribe) {
      unsubscribe();
    }
  });

  return {
    currentBuildNumber,
    localBuildNumber,
    needsRefresh,
    isCheckingBuild,
    checkBuildVersion,
    refreshApp,
    debugBuildNumbers,
    environment
  };
};

export { useBuildVersion }; 

